import { useEffect, useState } from "react";
import { SortableContainer, SortableElement } from "react-sortable-hoc";
import {
  useListContext,
  useDataProvider,
  Loading,
  Error,
  useRedirect,
  useRecordContext,
  useRefresh,
} from "react-admin";
import SaveIcon from "@mui/icons-material/Save";
import { arrayMoveImmutable } from "array-move";
import isEqual from "lodash/isEqual";

import resources from "../../constants/resources";
import {
  CONTENT_TYPES_LIST,
  HEADER_ID,
} from "../../constants/contentConstants";
import {
  ListItemField,
  HeaderField,
  IsPublishedHeaderField,
  PreviewHeaderField,
  IdField,
  ContentTypeField,
  PreviewField,
  IsPublishedField,
} from "../../styles/orderableList";
import {
  OrderableListStyles,
  AddAndOrderButtonStyles,
} from "../../styles/commonStyles";
import { SAVE_CONTENT_ORDER_LABEL } from "../../constants/titleConstants";
import DragHandle from "../common/DragHandle";

const HeaderItem = () => {
  const headerData = {
    [HEADER_ID]: {
      id: "Id",
      is_published: "Is Published",
      name_en_us: "Name",
      content_type: "Content Type",
    },
  };
  return (
    <ListItemField>
      <HeaderField record={headerData[HEADER_ID]} source="id" flexbasis="5" />
      <IsPublishedHeaderField
        record={headerData[HEADER_ID]}
        source="is_published"
      />
      <HeaderField
        record={headerData[HEADER_ID]}
        source="content_type"
        flexbasis="11"
      />
      <PreviewHeaderField record={headerData[HEADER_ID]} source="name_en_us" />
    </ListItemField>
  );
};

const SortableItem = SortableElement(({ currentId, data }) => (
  <ListItemField>
    <IdField record={data[currentId]} source="id" />
    <IsPublishedField record={data[currentId]} source="is_published" />
    <ContentTypeField
      record={data[currentId]}
      source="content_type"
      sortable={false}
      choices={CONTENT_TYPES_LIST}
    />
    <PreviewField record={data[currentId]} source="name_en" />

    <DragHandle />
  </ListItemField>
));

const SortableListContainer = SortableContainer(({ ...props }) => {
  const record = useRecordContext();
  return (
    <OrderableListStyles
      resource={resources.CONTENT.url}
      filter={{ topic: record.id }}
      perPage={200}
      pagination={false}
      actions={false}
      sort={{ field: "order", order: "ASC" }}
    >
      <SortableGrid setItems={props.setItems} items={props.items} {...props} />
    </OrderableListStyles>
  );
});

const SortableGrid = ({ ...props }) => {
  const { data = [] } = useListContext();
  const contentData = data.reduce((accumulator, item) => {
    accumulator[item.id] = item;
    return accumulator;
  }, {});
  const { items, setItems } = props;
  useEffect(() => {
    const ids = data.map(item => item.id);
    if (!isEqual([...[HEADER_ID].concat(ids)].sort(), [...items].sort())) {
      const orderSortedIds = Object.values(data)
        .sort((a, b) => a.order - b.order)
        .map(content => content.id);
      setItems([HEADER_ID].concat(orderSortedIds));
    }
  }, [data]);
  return (
    <div style={{ margin: "1em" }}>
      {items.map((id, index) => {
        if (id === HEADER_ID) {
          return <HeaderItem key={id} />;
        }
        return (
          <SortableItem
            key={id}
            currentId={id}
            data={contentData}
            index={index}
            value={id}
            {...props}
          />
        );
      })}
    </div>
  );
};

const SortableList = ({ ...props }) => {
  const record = useRecordContext();
  const [items, setItems] = useState([]);
  const [hasOrderChanged, setHasOrderChanged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const dataProvider = useDataProvider();
  const redirect = useRedirect();
  const refresh = useRefresh();

  const onSortEnd = ({ oldIndex, newIndex }) => {
    setItems(prevItems => arrayMoveImmutable(prevItems, oldIndex, newIndex));
    setHasOrderChanged(true);
  };

  const saveContentItemOrder = () => {
    setLoading(true);
    const contentItems = items.length > 0 ? items.slice(1) : items;
    const contentItemsIdObjs = contentItems.map(contentItem => ({
      id: contentItem,
    }));
    dataProvider
      .create(resources.CONTENTS_ORDERABLE.url, {
        data: contentItemsIdObjs,
      })
      .then(() => {
        setLoading(false);
        setHasOrderChanged(false);
        redirect(`/${resources.TOPIC.url}/${record.id}/contents`);
        refresh();
      })
      .catch(err => {
        setError(err);
        setLoading(false);
      });
  };

  return (
    <>
      <SortableListContainer
        {...props}
        onSortEnd={onSortEnd}
        useDragHandle
        lockAxis="y"
        items={items}
        setItems={setItems}
      />
      {error && <Error error={error} />}
      {loading ? (
        <Loading />
      ) : (
        <AddAndOrderButtonStyles
          variant="contained"
          onClick={saveContentItemOrder}
          title={SAVE_CONTENT_ORDER_LABEL}
          disabled={!hasOrderChanged}
          startIcon={<SaveIcon />}
        >
          {SAVE_CONTENT_ORDER_LABEL}
        </AddAndOrderButtonStyles>
      )}
    </>
  );
};

const ContentItemOrderableList = props => <SortableList {...props} />;

export default ContentItemOrderableList;
